import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { ALL_LEGAL_PAGE_KEYS, type LegalPageKey, type LegalPagesVisibility } from './types';

/**
 * Public visibility map for legal pages, controlled by the super-admin
 * (Settings → Public site → Legal pages). Missing keys default to visible so
 * a fresh install ships every page turned on until the operator hides some.
 *
 * Consumed by:
 *   - the SPA router (redirect hidden `/terms` /`/privacy` /… to `/404`)
 *   - LegalIndexPage (drop hidden rows from the overview list)
 *   - PublicFooter (drop hidden rows from the Legal column)
 *   - sitemap generation (skip hidden pages)
 *
 * Fallback while the fetch is in flight: every page visible. Otherwise a
 * marketing visitor could flash a 404 on a page that is actually published
 * because the JSON payload hasn't arrived yet.
 */
export function useLegalPagesVisible(): {
  visibility: LegalPagesVisibility;
  isVisible: (page: LegalPageKey) => boolean;
} {
  const { data } = useQuery({
    queryKey: ['legal-pages'],
    queryFn: () => api<LegalPagesVisibility>('/legal/pages', { auth: false }),
    staleTime: 5 * 60_000,
  });
  const visibility =
    data ??
    (Object.fromEntries(ALL_LEGAL_PAGE_KEYS.map((k) => [k, true])) as LegalPagesVisibility);
  return {
    visibility,
    isVisible: (page: LegalPageKey) => visibility[page] !== false,
  };
}
